/**
 * PlayerDrilldownContext — single global dialog mounted at the dashboard
 * root that any nested component can pop open via `usePlayerDrilldown()`.
 *
 * Usage:
 *   const { openPlayer } = usePlayerDrilldown();
 *   <button onClick={() => openPlayer(playerId)}>{name}</button>
 *
 * Or just drop in the `<PlayerName id={...}>{name}</PlayerName>` shortcut.
 */
import { createContext, useContext, useState, useCallback } from 'react';
import PlayerActivityDialog from './PlayerActivityDialog';

const Ctx = createContext({ openPlayer: () => {} });

export const PlayerDrilldownProvider = ({ children }) => {
    const [playerId, setPlayerId] = useState(null);

    const openPlayer = useCallback((id) => {
        if (!id) return;
        setPlayerId(String(id));
    }, []);
    const closeDialog = useCallback(() => setPlayerId(null), []);

    return (
        <Ctx.Provider value={{ openPlayer }}>
            {children}
            <PlayerActivityDialog
                playerId={playerId}
                open={!!playerId}
                onClose={closeDialog}
            />
        </Ctx.Provider>
    );
};

export const usePlayerDrilldown = () => useContext(Ctx);

/**
 * Drop-in replacement for any plain text player name. Makes the name a
 * clickable element that pops the activity dialog. Falls back to a plain
 * span if no id is provided so legacy/null-safe rendering still works.
 */
export const PlayerName = ({
    id,
    children,
    className = '',
    title = 'View player report',
    testIdPrefix = 'player-name',
    onClick,
    ...rest
}) => {
    const { openPlayer } = usePlayerDrilldown();
    if (!id) return <span className={className} {...rest}>{children}</span>;
    return (
        <button
            type="button"
            onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick(e);
                if (!e.defaultPrevented) openPlayer(id);
            }}
            title={title}
            data-testid={`${testIdPrefix}-${id}`}
            className={`text-left hover:underline decoration-dotted underline-offset-2 hover:text-white transition cursor-pointer ${className}`}
            {...rest}
        >
            {children}
        </button>
    );
};
